/* workspace-admin/Settings — M-06: archive/delete workflow.
   Archive is always available for ACTIVE/ONBOARDING workspaces.
   Delete is only shown (and enabled) once workspace is ARCHIVED.
   Backend enforces: delete requires ARCHIVED; archive endpoint is POST /workspaces/:wsId/archive. */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, ConfirmDialog } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { Gate } from '../rbac/guards';
import { PageHead } from '../platform/_ui';
import { findWorkspace } from '../platform/platformStore';
import * as workspaceApi from '../api/workspace.api.js';

const SectionCard = ({ title, desc, children }) => (
  <div className="nx-panel" style={{ padding: 20, marginBottom: 16, maxWidth: 720 }}>
    <div style={{ marginBottom: 14 }}>
      <h3 className="nx-panel-title">{title}</h3>
      {desc && <p className="nx-muted nx-mini" style={{ marginTop: 2 }}>{desc}</p>}
    </div>
    {children}
  </div>
);

export default function Settings({ data }) {
  const { wsId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete,  setConfirmDelete]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [deleting,  setDeleting]  = useState(false);

  const ws = findWorkspace(data.state, wsId);

  /* All hooks before conditional return */
  if (!ws) return null;

  const isArchived  = ws.status === 'ARCHIVED';
  const isSuspended = ws.status === 'SUSPENDED';
  const isLocked    = isArchived || isSuspended;

  const localUpdate = (patch) =>
    data.update(s => ({ ...s, workspaces: s.workspaces.map(w => w.id === wsId ? { ...w, ...patch } : w) }));

  const saveName = async (name) => {
    if (!name || name === ws.name) return;
    setSaving(true);
    try {
      await workspaceApi.update(wsId, { name });
      localUpdate({ name });
      toast({ tone: 'success', icon: 'Check', title: 'Name updated' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err.message });
    } finally {
      setSaving(false);
    }
  };

  /* M-06: archive — sets status ARCHIVED; delete requires ARCHIVED first */
  const archiveWorkspace = async () => {
    setArchiving(true);
    try {
      await workspaceApi.archive(wsId);
      localUpdate({ status: 'ARCHIVED' });
      data.addAudit({ action: 'archived workspace', target: ws.name, cat: 'Org', sev: 'warning' });
      toast({ tone: 'warning', icon: 'Archive', title: 'Workspace Archived', desc: ws.name + ' is now read-only.' });
      setConfirmArchive(false);
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Archive failed', desc: err.message });
    } finally {
      setArchiving(false);
    }
  };

  /* M-06: delete — only if ARCHIVED */
  const deleteWorkspace = async () => {
    setDeleting(true);
    try {
      await workspaceApi.remove(wsId);
      data.update(s => ({
        ...s,
        workspaces: s.workspaces.filter(w => w.id !== wsId),
        memberships: s.memberships.filter(mb => mb.workspace !== wsId),
      }));
      data.addAudit({ action: 'deleted workspace', target: ws.name, cat: 'Org', sev: 'critical' });
      toast({ tone: 'warning', icon: 'Trash', title: 'Workspace Deleted', desc: ws.name });
      navigate('/workspaces');
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Delete failed', desc: err.message });
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="nx-cc-page">
      <PageHead title="Workspace Settings" sub={'Configuration for ' + ws.name} />

      {isSuspended && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--nx-amber-50,#fffbeb)', border: '1px solid var(--nx-amber-200,#fde68a)', color: 'var(--nx-amber-800,#92400e)' }}>
          <NexusIcon name="PauseCircle" size={16} />
          <span style={{ fontSize: 13 }}>This workspace is <strong>suspended</strong>. Settings changes are disabled while suspended.</span>
        </div>
      )}
      {isArchived && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--nx-surface-2,#f1f5f9)', border: '1px solid var(--nx-border,#e2e8f0)', color: 'var(--nx-text-muted,#64748b)' }}>
          <NexusIcon name="Archive" size={16} />
          <span style={{ fontSize: 13 }}>This workspace is <strong>archived</strong>. Settings are read-only. You may delete it below.</span>
        </div>
      )}

      <SectionCard title="General">
        <div className="nx-form">
          <div className="nx-form-row">
            <label htmlFor="ws-name">Workspace Name</label>
            <input
              id="ws-name"
              className="nx-input"
              defaultValue={ws.name}
              disabled={saving || isLocked}
              aria-label="Workspace name"
              onBlur={e => saveName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            />
          </div>
        </div>
      </SectionCard>

      <Gate action="workspace.delete">
        <SectionCard
          title="Danger Zone"
          desc="Irreversible actions. Archive makes the workspace read-only and removes CRM access. Deletion is permanent."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Archive — only when workspace is not already archived/suspended */}
            {!isArchived && !isSuspended && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Archive Workspace</div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                    Makes this workspace read-only. Members lose CRM access. Can be restored later.
                  </div>
                </div>
                <Btn variant="warning" icon="Archive" aria-label={'Archive workspace ' + ws.name}
                  onClick={() => setConfirmArchive(true)} style={{ flexShrink: 0 }}>
                  Archive
                </Btn>
              </div>
            )}

            {/* Delete — only when ARCHIVED */}
            {isArchived && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>Delete Workspace</div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                    Permanently removes this workspace and all its memberships. This cannot be undone.
                  </div>
                </div>
                <Btn variant="danger" icon="Trash" aria-label={'Delete workspace ' + ws.name}
                  onClick={() => setConfirmDelete(true)} style={{ flexShrink: 0 }}>
                  Delete
                </Btn>
              </div>
            )}

            {/* Guidance when ACTIVE — show archive-first requirement */}
            {!isArchived && !isSuspended && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 6, background: 'var(--nx-surface-2,#f1f5f9)', fontSize: 12, color: 'var(--nx-text-muted)' }}>
                <NexusIcon name="Info" size={13} />
                <span>To permanently delete this workspace, archive it first.</span>
              </div>
            )}
          </div>
        </SectionCard>
      </Gate>

      {confirmArchive && (
        <ConfirmDialog
          title={'Archive ' + ws.name + '?'}
          desc="This makes the workspace read-only. All members will lose CRM access immediately. You can restore it later from this settings page."
          confirmLabel="Archive Workspace"
          busy={archiving}
          onConfirm={archiveWorkspace}
          onCancel={() => { if (!archiving) setConfirmArchive(false); }}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={'Permanently delete ' + ws.name + '?'}
          desc="This removes the workspace, all memberships, and associated data. This action cannot be undone."
          danger
          confirmLabel="Delete Workspace"
          busy={deleting}
          onConfirm={deleteWorkspace}
          onCancel={() => { if (!deleting) setConfirmDelete(false); }}
        />
      )}
    </div>
  );
}
